import Vue from 'vue'
import Router from 'vue-router'
import store from './store/store'
import i18n from '@/i18n/i18n.js'
import {
  CHECK_AUTH,
  LOGOUT
}
from './store/action.types'
import { API_URL } from '@/common/config'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return {
      x: 0,
      y: 0
    }
  },
  routes: [

    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [{
        path: '/',
        // redirect: '/branches/1', // roles == 'company' ? 'branches' : 'profile'
        name: 'indexPage',
        component: () => import('./views/IndexPageRedirect.vue'),
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/branches/:id',
        name: 'branchDetail',
        component: () => import('./views/branches/BranchDetail.vue'),
        meta: {
          rule: 'company',
          isGrayBackground: true,
          title_i18n: 'pages.profile'
        }
      },
      {
        path: '/branches/:id/profile',
        name: 'branchDetailProfile',
        component: () => import('./views/branches/BranchDetail.vue'),
        meta: {
          rule: 'company',
          isGrayBackground: true,
          title_i18n: 'pages.profile'
        }
      },
      {
        path: '/branches/:id/employees',
        name: 'branchDetailEmployee',
        component: () => import('./views/branches/BranchEmployees.vue'),
        meta: {
          rule: 'company',
          title_i18n: 'pages.employees'
        }
      },
      {
        path: '/branch/add',
        name: 'branchAdd',
        component: () => import('./views/branches/BranchAdd.vue'),
        meta: {
          rule: 'company',
          isGrayBackground: true,
          title_i18n: 'pages.newBranch'
        }
      },
      {
        path: '/branches/:id/qr',
        name: 'branchQr',
        component: () => import('./views/branches/BranchCommon.vue'),
        meta: {
          rule: 'company',
          title_i18n: 'pages.generalPage'
        }
      },
      {
        path: '/tips/:id/',
        name: 'branchTips',
        component: () => import('./views/branches/BranchEmployeeTipsTable.vue'),
        meta: {
          rule: 'company',
          title_i18n: 'pages.tipAnalytics'
        }
      },
      {
        path: '/profile',
        name: 'employeeProfile',
        component: () => import('./views/employee/EmployeeProfile.vue'),
        meta: {
          rule: 'employee',
          isGrayBackground: true,
          title_i18n: 'pages.profile'
        },
        children: [{
          path: ':id',
          name: 'employeeProfileDetail',
          component: () => import('./views/employee/EmployeeProfile.vue'),
          meta: {
            rule: 'company',
            isGrayBackground: true,
            title_i18n: 'pages.profile'
          }
        }]
      },
      {
        path: '/employee_qr',
        name: 'employeeQR',
        component: () => import('./views/employee/DownloadQR.vue'),
        meta: {
          rule: 'employee',
          title_i18n: 'pages.qrCode'
        }
      },
      {
        path: '/tips',
        name: 'employeeTips',
        component: () => import('./views/employee/EmployeeTips.vue'),
        meta: {
          rule: 'employee',
          title_i18n: 'pages.myTips'
        },
        children: [{
          path: ':employeeID',
          name: 'employeeTipsDetail',
          component: () => import('./views/employee/EmployeeTips.vue'),
          meta: {
            rule: 'company',
            title_i18n: 'pages.tipAnalytics'
          }
        }]
      },
      {
        path: '/logout',
        name: 'logout',
        component: () => import('./views/auth/Logout.vue'),
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/change_branch',
        name: 'employeeChangeBranch',
        component: () => import('./views/employee/ChangeBranch.vue'),
        meta: {
          rule: 'employee',
          title_i18n: 'pages.myCompany'
        }
      }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [{
        path: '/login',
        name: 'pageLogin',
        component: () => import('@/views/auth/Login.vue'),
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/confirm/branch/employee/:token',
        name: 'confirmBranchEmployee',
        component: () => import('@/views/auth/components/ConfirmBranchEmployeeByInvitation.vue'),
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/confirm/branch/invite/employee/:token',
        name: 'confirmBranchInviteEmployee',
        component: () => import('@/views/auth/components/ConfirmBranchInviteEmployee.vue'),
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/register_company',
        name: 'companyRegistration',
        component: () => import('@/views/auth/components/CompanyRegistrationForm.vue'),
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/error-404',
        name: 'pageError404',
        beforeEnter: (to, from, next) => {
          window.location.replace('https://kontur.ru/lp/market-tips/notfound')
        },
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/not-authorized',
        name: 'pageNotAuthorized',
        component: () => import('@/views/auth/NotAuthorized.vue'),
        meta: {
          rule: 'public'
        }
      },
      {
        path: '/register-complete',
        name: 'registerComplete',
        component: () => import('@/views/auth/RegisterComplete.vue'),
        meta: {
          rule: 'public'
        }
      }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/error-404'
    }
  ]
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.hidden = true
  }
})

router.beforeEach((to, from, next) => {
  const pagePostfix = to.meta.title_i18n ? ` | ${i18n.t(to.meta.title_i18n)}` : ''
  document.title = `Контур.Маркет Чаевые${pagePostfix}`

  if (to.path.startsWith('/confirm')) {
    if (to.path.startsWith('/confirm/register')) {
      store.dispatch(LOGOUT).then(() => {
        next()
      })
    } else {
      next()
    }
  } else if (
    // we are tring to reach public paths
    to.path === '/login' ||
    to.path === '/not-authorized' ||
    to.path === '/forgot-password' ||
    to.path === '/error-404' ||
    to.path === '/error-500' ||
    to.path === '/register' ||
    to.path === '/callback' ||
    to.path === '/covid' ||
    to.path === '/register-complete' ||
    to.path === '/iframe' ||
    to.path.startsWith('/payment') ||
    to.path.startsWith('/register_company')
  ) {
    return next()
  } else if (store.getters.isAuthenticated) {
    store.dispatch(CHECK_AUTH).then(() => {
      return next()
    })
  } else {
    window.location.href = API_URL + '/login'
  }
})

export default router
